import { useState } from "react";
import { Badge, Box, Button, Card, Code, Group, Progress, SegmentedControl, SimpleGrid, Stack, Tabs, Text, Title, UnstyledButton } from "@mantine/core";
import { IconFileTypePdf, IconShoppingCart } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { fmtNumber, type ShoppingItem } from "@passio/api-client";
import { ingredientGrams, ingredientLabel, instructionSteps, isLinked, type MacroTotals, type MealPlan, type MealPlanDay, type MealPlanHousehold, type MealPlanMeal, type MealPlanPdf } from "@passio/easyai-client";
import { MacroChips, StatCard } from "../theme/components";
import { FoodIcon } from "./common";
import { FoodDetailDrawer } from "./food";
import type { ShoppingHandoff } from "../pages/Shopping";

const BAND: Record<string, string> = {
  breakfast: "#d97706",
  brunch: "#d97706",
  lunch: "#3F8F1F",
  dinner: "#374e8e",
  supper: "#374e8e",
  snack: "#0f766e",
  pre_workout: "#a14a4a",
  post_workout: "#a14a4a",
};
const bandColor = (mealName?: string) => {
  const k = (mealName ?? "").toLowerCase().replace(/[^a-z]+/g, "_").replace(/_?\d+$/, "");
  return BAND[k] ?? "#141C2F";
};
const titleCase = (s: string) => s.replace(/_/g, " ").replace(/(^|\s)\S/g, (c) => c.toUpperCase());
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x : typeof x === "object" && x ? String((x as Record<string, unknown>).name ?? JSON.stringify(x)) : String(x))) : []);

/** Preferences the chain extracted from the request (constraints), as readable chips. */
function constraintChips(c: Record<string, unknown>): { label: string; value: string; color: string }[] {
  const out: { label: string; value: string; color: string }[] = [];
  const add = (label: string, v: unknown, color = "gray") => {
    const list = typeof v === "string" ? (v.trim() ? [v] : []) : strList(v);
    if (list.length) out.push({ label, value: list.slice(0, 8).join(", "), color });
  };
  add("Diet", c.diet_style, "lime.8");
  add("Allergies", c.allergies, "red");
  add("Avoid", c.dislikes ?? c.exclusions, "orange");
  add("Likes", c.likes, "lime.8");
  const budget = c.budget;
  add("Budget", typeof budget === "object" && budget ? (budget as Record<string, unknown>).level : budget);
  const schedule = c.schedule as Record<string, unknown> | undefined;
  add("Cooking time", schedule?.cooking_time);
  const prefs = c.preferences as Record<string, unknown> | undefined;
  add("Cuisines", prefs?.cuisines);
  const household = c.household as Record<string, unknown> | undefined;
  if (household && typeof household === "object") add("Household", household.size ? `${household.size} people` : undefined);
  return out;
}

const MACROS: { key: keyof MacroTotals; label: string; unit: string }[] = [
  { key: "calories", label: "Calories", unit: "kcal" },
  { key: "protein_g", label: "Protein", unit: "g" },
  { key: "carbs_g", label: "Carbs", unit: "g" },
  { key: "fat_g", label: "Fat", unit: "g" },
];

/** Planned vs target per macro, colored by how close the day landed. */
function TargetBars({ target, planned }: { target?: MacroTotals; planned?: MacroTotals }) {
  if (!planned && !target) return null;
  return (
    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
      {MACROS.map((m) => {
        const t = num(target?.[m.key]);
        const p = num(planned?.[m.key]);
        const ratio = t && p !== undefined ? p / t : undefined;
        const off = ratio === undefined ? 0 : Math.abs(1 - ratio);
        const color = ratio === undefined ? "gray" : off <= 0.1 ? "lime.8" : off <= 0.25 ? "orange" : "red";
        return (
          <Box key={m.key}>
            <Group justify="space-between" gap={4}>
              <Text fz="xs" c="dimmed" fw={600}>
                {m.label}
              </Text>
              <Text fz="xs" ff="monospace">
                {p !== undefined ? fmtNumber(p) : "–"}
                {t ? ` / ${fmtNumber(t)}` : ""} {m.unit}
              </Text>
            </Group>
            <Progress value={ratio === undefined ? 0 : Math.min(100, ratio * 100)} color={color} size="sm" mt={4} />
          </Box>
        );
      })}
    </SimpleGrid>
  );
}

const SERVES = /^serves\s+(\d+)\s*[—–:-]?\s*/i;

/** Family recipes start with "Serves N — multiply…"; that becomes a badge instead of a step. */
function splitServes(steps: string[]): { serves?: number; steps: string[] } {
  const m = steps[0]?.match(SERVES);
  if (!m) return { steps };
  const rest = steps[0].replace(SERVES, "").replace(/^multiply[^.]*\.?\s*/i, "").trim();
  return { serves: Number(m[1]), steps: rest ? [rest, ...steps.slice(1)] : steps.slice(1) };
}

const hasKcal = (t?: MacroTotals) => (num(t?.calories) ?? 0) > 0;
const times = (t: MacroTotals | null | undefined, f: number): MacroTotals | undefined =>
  t ? (Object.fromEntries(Object.entries(t).map(([k, v]) => [k, typeof v === "number" ? v * f : v])) as MacroTotals) : undefined;

/** Household plans: amounts for one person, or for everyone at the table. */
export type PortionView = "person" | "household";

function MealCard({ meal, people, view, onFood }: { meal: MealPlanMeal; people?: number; view: PortionView; onFood: (refCode: string, name: string, grams?: number) => void }) {
  const split = splitServes(instructionSteps(meal.instructions));
  const steps = split.steps;
  // A household plan serves the household, whatever number the recipe step wrote.
  const serves = people ?? (split.serves && split.serves > 1 ? split.serves : undefined);
  const perPerson = Boolean(people);
  const whole = view === "household" && people !== undefined && people > 1;
  const f = whole ? people : 1;
  const ings = meal.ingredients_detailed ?? [];
  const mt = times(meal.macro_totals, f);
  return (
    <div className="pd-meal-card">
      <div className="pd-meal-band" style={{ background: bandColor(meal.mealName) }}>
        <div>
          <div className="kind">{titleCase(meal.mealName ?? "Meal")}</div>
          <Text fw={700} fz="lg" c="white" lh={1.25}>
            {meal.recipeTitle || meal.title || titleCase(meal.mealName ?? "Meal")}
          </Text>
        </div>
        {serves ? (
          <Badge color="dark" variant="white" c="ink.8" size="lg" tt="none" style={{ flex: "none" }}>
            Serves {serves}
          </Badge>
        ) : null}
      </div>
      <Box p="md">
        {hasKcal(mt) && (
          <Group gap="xs">
            <MacroChips calories={num(mt!.calories)} protein={num(mt!.protein_g)} carbs={num(mt!.carbs_g)} fat={num(mt!.fat_g)} size="sm" />
            {perPerson && (
              <Text fz="xs" c="dimmed">
                {whole ? `for ${people}` : "per person"}
              </Text>
            )}
          </Group>
        )}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mt={hasKcal(mt) ? "md" : 0}>
          <div>
            <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={4} style={{ letterSpacing: "0.05em" }}>
              Ingredients{whole ? ` · for ${people}` : perPerson ? " · per person" : ""}
            </Text>
            {ings.length === 0 && (
              <Text fz="sm" c="dimmed">
                No ingredient details.
              </Text>
            )}
            {ings.map((it, i) => {
              const food = it.passio_food;
              const name = ingredientLabel(it) || "Ingredient";
              const grams = ingredientGrams(it);
              const kcal = num(it.macros?.calories);
              const body = (
                <Group gap="sm" wrap="nowrap" className="pd-ing-row">
                  <FoodIcon iconId={food?.iconId} size={32} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fz="sm" fw={600} truncate>
                      {titleCase(name)}
                    </Text>
                    {food?.displayName && food.displayName.toLowerCase() !== name.toLowerCase() && (
                      <Text fz="xs" c="dimmed" truncate>
                        Passio: {food.displayName}
                      </Text>
                    )}
                  </Box>
                  <Box ta="right" style={{ whiteSpace: "nowrap" }}>
                    <Text fz="xs" ff="monospace" c="dimmed">
                      {grams !== undefined ? `${fmtNumber(grams * f)} g` : ""}
                      {kcal !== undefined ? ` · ${fmtNumber(kcal * f)} kcal` : ""}
                    </Text>
                    {people && people > 1 && grams !== undefined && (
                      <Text fz={11} ff="monospace" c="dimmed" opacity={0.75}>
                        {whole ? `${fmtNumber(grams)} g each` : `${fmtNumber(grams * people)} g for ${people}`}
                      </Text>
                    )}
                  </Box>
                </Group>
              );
              return food?.refCode ? (
                <UnstyledButton key={i} w="100%" onClick={() => onFood(food.refCode!, food.displayName ?? name, grams !== undefined ? grams * f : undefined)} aria-label={`Nutrition for ${name}`}>
                  {body}
                </UnstyledButton>
              ) : (
                <Box key={i}>{body}</Box>
              );
            })}
          </div>
          <div>
            <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={4} style={{ letterSpacing: "0.05em" }}>
              Method
            </Text>
            {steps.length === 0 ? (
              <Text fz="sm" c="dimmed">
                No instructions.
              </Text>
            ) : (
              <Stack gap={8}>
                {steps.map((s, i) => (
                  <Group key={i} gap="sm" wrap="nowrap" align="flex-start">
                    <Badge size="md" circle color="ink.8" variant="filled" style={{ flex: "none" }}>
                      {i + 1}
                    </Badge>
                    <Text fz="sm">{s}</Text>
                  </Group>
                ))}
              </Stack>
            )}
          </div>
        </SimpleGrid>
      </Box>
    </div>
  );
}

function DayPanel({ day, macroTargets, people, view, onFood }: { day: MealPlanDay; macroTargets: MacroTotals | null; people?: number; view: PortionView; onFood: (refCode: string, name: string, grams?: number) => void }) {
  const whole = view === "household" && people !== undefined && people > 1;
  const f = whole ? people : 1;
  const target = times(day.macro_target ?? macroTargets, f);
  return (
    <Stack gap="md">
      {hasKcal(day.macro_totals) && (
        <Card>
          <Text fw={700} mb="sm">
            Day {day.day_number ?? ""} · {hasKcal(target) ? "planned vs target" : "planned"}
            {whole ? ` (for ${people})` : people ? " (per person)" : ""}
          </Text>
          <TargetBars target={hasKcal(target) ? target : undefined} planned={times(day.macro_totals, f)} />
          {whole && hasKcal(target) && (
            <Text fz="xs" c="dimmed" mt="xs">
              Target shown as {people} × one adult&apos;s; children usually need less.
            </Text>
          )}
        </Card>
      )}
      {day.meals.map((m, i) => (
        <MealCard key={i} meal={m} people={people} view={view} onFood={onFood} />
      ))}
    </Stack>
  );
}

function HouseholdStrip({ household }: { household: MealPlanHousehold }) {
  const c = household.constraints;
  const allergies = strList(c.allergies);
  return (
    <Stack gap="xs">
      <Text className="pd-dim" ff="monospace" fz="xs">
        household · {household.size ?? household.members.length} {(household.size ?? household.members.length) === 1 ? "person" : "people"}
        {allergies.length ? ` · avoiding ${allergies.join(", ")} for everyone` : ""}
      </Text>
      <Group gap="sm" align="stretch">
        {household.members.map((m, i) => {
          const sub = [m.role && m.role !== "other" ? titleCase(m.role) : null, typeof m.age === "number" ? `${m.age} yrs` : null].filter(Boolean).join(" · ");
          const notes = [...strList(m.allergies).map((a) => `Allergic: ${a}`), ...strList(m.dislikes).map((d) => `Dislikes: ${d}`), ...strList(m.preferences)];
          return (
            <div key={i} className="pd-member">
              <Text fw={700} fz="sm" c="white">
                {m.name || `Member ${i + 1}`}
              </Text>
              {sub && (
                <Text fz="xs" className="pd-dim">
                  {sub}
                </Text>
              )}
              {notes.slice(0, 3).map((n) => (
                <Text key={n} fz="xs" c={n.startsWith("Allergic") ? "red.3" : "gray.4"}>
                  {n}
                </Text>
              ))}
            </div>
          );
        })}
      </Group>
    </Stack>
  );
}

/** Every planned ingredient, summed by name, as a shopping list (scaled up to the household when there is one). */
function shoppingList(plan: MealPlan, servings: number): ShoppingItem[] {
  const by = new Map<string, ShoppingItem>();
  for (const d of plan.days)
    for (const m of d.meals)
      for (const it of m.ingredients_detailed ?? []) {
        const name = ingredientLabel(it).toLowerCase();
        if (!name) continue;
        const grams = (ingredientGrams(it) ?? 0) * servings;
        const cur = by.get(name) ?? { name, portionQuantity: 0, portionSize: "g" };
        cur.portionQuantity = Math.round((cur.portionQuantity ?? 0) + grams);
        by.set(name, cur);
      }
  return [...by.values()].map((i) => (i.portionQuantity ? i : { name: i.name, portionQuantity: 1, portionSize: "each" }));
}

export function MealPlanView({ plan, pdf, title, household }: { plan: MealPlan; pdf: MealPlanPdf | null; title: string; household?: MealPlanHousehold | null }) {
  const navigate = useNavigate();
  const [food, setFood] = useState<{ refCode: string; name: string; grams?: number } | null>(null);
  const meals = plan.days.reduce((n, d) => n + d.meals.length, 0);
  const totalled = plan.days.map((d) => d.macro_totals).filter((m) => (num(m?.calories) ?? 0) > 0);
  const mean = (k: "calories" | "protein_g" | "carbs_g" | "fat_g") =>
    totalled.length ? totalled.reduce((a, m) => a + (num(m?.[k]) ?? 0), 0) / totalled.length : undefined;
  const avg = mean("calories");
  const chips = constraintChips(plan.constraints);
  const warnings = [...new Set([...strList(plan.constraints.warnings), ...strList(household?.constraints.warnings)])];
  const onFood = (refCode: string, name: string, grams?: number) => setFood({ refCode, name, grams });
  const people = household ? (household.size ?? household.members.length) || 1 : 1;
  const perPerson = Boolean(household);
  const [view, setView] = useState<PortionView>("person");
  const whole = perPerson && people > 1 && view === "household";
  const f = whole ? people : 1;
  const ings = plan.days.flatMap((d) => d.meals.flatMap((m) => m.ingredients_detailed ?? []));
  const linked = ings.filter(isLinked).length;
  const unmatched = ings.length - linked;
  const t = hasKcal(plan.macro_targets ?? undefined) ? plan.macro_targets : plan.days.find((d) => hasKcal(d.macro_target ?? undefined))?.macro_target;
  const targetCard = hasKcal(t ?? undefined) || avg;
  const g = (target: unknown, average?: number) => {
    const v = hasKcal(t ?? undefined) ? num(target) : average;
    return v ? `${fmtNumber(v * f)} g` : "–";
  };
  const scope = whole ? ` · for ${people}` : perPerson ? " · per person" : "";

  return (
    <Stack gap="lg">
      <Box className="pd-dark" p={{ base: 20, sm: 28 }} style={{ borderRadius: 20 }}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="lg">
          <Stack gap={8} maw={680} style={{ flex: 1, minWidth: 260 }}>
            <Text className="pd-dim" ff="monospace" fz="xs">
              {title} meal plan
            </Text>
            <Title order={2} c="white" fz={{ base: 24, sm: 30 }}>
              {plan.days.length} day{plan.days.length === 1 ? "" : "s"} · {meals} meals{household ? ` · for ${people}` : ""}
            </Title>
            {chips.length > 0 && (
              <Group gap={6}>
                {chips.map((c) => (
                  <Badge key={c.label} color={c.color} variant="light" tt="none" size="lg" fw={500}>
                    <b>{c.label}:</b> {c.value}
                  </Badge>
                ))}
              </Group>
            )}
            <Group gap="xs" mt={4}>
              {pdf?.pdfUrl && (
                <Button component="a" href={pdf.pdfUrl} target="_blank" rel="noopener" color="lime" c="ink.8" leftSection={<IconFileTypePdf size={16} />}>
                  Printable PDF
                </Button>
              )}
              <Button
                variant="outline"
                color="gray.0"
                leftSection={<IconShoppingCart size={16} />}
                onClick={() => navigate("/shopping", { state: { items: shoppingList(plan, people), title: `${title} meal plan` } satisfies ShoppingHandoff })}
              >
                Shopping list{household ? ` (×${people})` : ""}
              </Button>
            </Group>
            {pdf?.skipped && (
              <Text className="pd-dim" fz="xs">
                PDF skipped for this run.
              </Text>
            )}
          </Stack>
          {targetCard ? (
            <Stack gap="sm" w={{ base: "100%", sm: 280 }}>
              <StatCard
                caption={hasKcal(t ?? undefined) ? `daily targets${scope}` : `average day${scope}`}
                title={hasKcal(t ?? undefined) ? `${fmtNumber(t!.calories! * f)} kcal` : `≈ ${fmtNumber(avg! * f)} kcal`}
                stats={[
                  { value: g(t?.protein_g, mean("protein_g")), label: "protein" },
                  { value: g(t?.carbs_g, mean("carbs_g")), label: "carbs" },
                  { value: g(t?.fat_g, mean("fat_g")), label: "fat" },
                ]}
                accent
              />
            </Stack>
          ) : null}
        </Group>
        {household && household.members.length > 0 && (
          <Box mt="lg">
            <HouseholdStrip household={household} />
          </Box>
        )}
      </Box>
      {ings.length > 0 && linked === 0 && (
        <div className="pd-banner info">
          <span className="bic" aria-hidden>
            i
          </span>
          <div>
            <Text fw={700}>No ingredients were matched to Passio foods</Text>
            <Text fz="sm">The recipes came back, but the chain&apos;s Passio link step matched none of the ingredients, so there are no food icons or nutrition totals for this plan.</Text>
          </div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="pd-banner urgent">
          <span className="bic" aria-hidden>
            !
          </span>
          <div>
            <Text fw={700}>Worth knowing</Text>
            {warnings.map((w) => (
              <Text key={w} fz="sm">
                {w}
              </Text>
            ))}
          </div>
        </div>
      )}
      {linked > 0 && unmatched > 0 && (
        <Text fz="xs" c="dimmed">
          {linked} of {ings.length} ingredients matched to Passio foods. The {unmatched} without an icon aren&apos;t counted in the nutrition totals.
        </Text>
      )}
      {perPerson && people > 1 && (
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Text fz="sm" c="dimmed">
            {whole ? `Amounts and nutrition for all ${people} people at the table.` : "Amounts and nutrition for one person's plate."}
          </Text>
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as PortionView)}
            data={[
              { value: "person", label: "Per person" },
              { value: "household", label: `Whole household (${people})` },
            ]}
          />
        </Group>
      )}
      {plan.days.length === 1 ? (
        <DayPanel day={plan.days[0]} macroTargets={plan.macro_targets} people={household ? people : undefined} view={view} onFood={onFood} />
      ) : (
        <Tabs defaultValue="0" variant="pills" radius="md" keepMounted={false}>
          <Tabs.List mb="md">
            {plan.days.map((d, i) => (
              <Tabs.Tab key={i} value={String(i)}>
                Day {d.day_number ?? i + 1}
                {num(d.macro_totals?.calories) ? (
                  <Text span fz="xs" c="dimmed" ml={6}>
                    {fmtNumber(d.macro_totals!.calories! * f)} kcal
                  </Text>
                ) : null}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {plan.days.map((d, i) => (
            <Tabs.Panel key={i} value={String(i)}>
              <DayPanel day={d} macroTargets={plan.macro_targets} people={household ? people : undefined} view={view} onFood={onFood} />
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
      <Text fz="xs" c="dimmed">
        Ingredients are matched to Passio foods by the chain&apos;s <Code>link_to_passio_ingredients</Code> step; tap one for its full nutrition.
      </Text>
      {food && <FoodDetailDrawer refCode={food.refCode} name={food.name} amount={{ weightGrams: food.grams }} onClose={() => setFood(null)} />}
    </Stack>
  );
}
